/**
 * Resolvedor canônico de chaves e seções de briefing Unik.
 * Garante compatibilidade 100% entre:
 * 1. Formulário público de onboarding /cadastro (chaves pt-br);
 * 2. Importação oficial ClickUp / ERPigs (chaves en);
 * 3. Schema legado BRIEFING_SCHEMA.
 */

export interface BriefingFieldDef {
  key: string;
  label: string;
  hint?: string;
  rows?: number;
}

export interface BriefingChapterDef {
  id: number;
  title: string;
  subtitle: string;
  color: string;
  fields: BriefingFieldDef[];
}

export const STRATEGIC_BRIEFING_CHAPTERS: BriefingChapterDef[] = [
  {
    id: 1,
    title: "1. Identificação e Acesso",
    subtitle: "Dados de contato e criação de acesso à plataforma Unik.",
    color: "#3b82f6",
    fields: [
      { key: "name", label: "Nome Completo" },
      { key: "instagram", label: "Instagram (@)" },
      { key: "whatsapp", label: "WhatsApp com DDD" },
      { key: "email", label: "E-mail de Acesso" },
      { key: "businessName", label: "Nome da Empresa / Clínica" },
      { key: "city", label: "Cidade / Estado" },
      { key: "segment", label: "Segmento de Atuação" },
    ],
  },
  {
    id: 2,
    title: "2. Posicionamento e Diferenciação",
    subtitle: "Como você se destaca e como sua marca deve ser percebida.",
    color: "#8b5cf6",
    fields: [
      {
        key: "room_behavior",
        label: "Se sua marca fosse uma pessoa, como ela chegaria numa sala cheia de concorrentes?",
        rows: 4,
      },
      {
        key: "best_skill_90",
        label: "O que você faz melhor que 90% do seu mercado, mesmo que ninguém ainda saiba?",
        rows: 4,
      },
      {
        key: "never_sound_like",
        label: "O que você não quer parecer nas redes sociais de jeito nenhum?",
        rows: 3,
      },
      {
        key: "why_choose_you",
        label: "Por que as pessoas compram de você? Qual é o seu verdadeiro diferencial?",
        rows: 4,
      },
      {
        key: "expensive_reason",
        label: "O que faria alguém te escolher mesmo sendo a opção mais cara?",
        rows: 4,
      },
    ],
  },
  {
    id: 3,
    title: "3. Missão, Crenças e História",
    subtitle: "A alma da sua marca, valores inegociáveis e pontos de vista fortes.",
    color: "#ec4899",
    fields: [
      {
        key: "mission",
        label: "Qual é a missão da sua marca? O que ela defende com unhas e dentes?",
        rows: 4,
      },
      {
        key: "mantra",
        label: "Se sua marca tivesse um mantra oficial, qual seria?",
        rows: 3,
      },
      {
        key: "origin_story",
        label: "Qual é a história de criação da sua marca?",
        rows: 4,
      },
      {
        key: "controversial_opinion",
        label: "Qual é a sua opinião mais polêmica, sincera ou divisora de águas sobre o seu nicho?",
        rows: 4,
      },
      {
        key: "transformation_30d",
        label: "Se alguém te acompanhasse todos os dias por 30 dias seguidos, o que você gostaria que essa pessoa pensasse sobre si mesma e sobre a vida?",
        rows: 4,
      },
    ],
  },
  {
    id: 4,
    title: "4. Conexão e Percepção no Instagram",
    subtitle: "A mensagem central do perfil, estética visual e tom de conversa.",
    color: "#c2a06b",
    fields: [
      {
        key: "instagram_purpose",
        label: "Se o seu perfil no Instagram tivesse apenas uma missão na vida do seu seguidor, qual seria?",
        rows: 4,
      },
      {
        key: "first_impression",
        label: "Qual é a primeira impressão que uma pessoa deve ter ao entrar no seu Instagram?",
        rows: 4,
      },
      {
        key: "evolution_goal",
        label: "O que você espera que mude no seu perfil depois de trabalhar comigo?",
        rows: 4,
      },
      {
        key: "stories_pride",
        label: "Que tipo de coisa você postaria nos Stories e sentiria: 'caramba, isso ficou muito bom'?",
        rows: 4,
      },
      {
        key: "save_reason",
        label: "O que faria alguém salvar um post seu para ler de novo depois?",
        rows: 4,
      },
    ],
  },
  {
    id: 5,
    title: "5. Psicologia e Conversão do Cliente",
    subtitle: "Entendendo quem compra, dores secretas e motivações reais.",
    color: "#10b981",
    fields: [
      {
        key: "target_audience",
        label: "Com que tipo de cliente você quer se conectar através do seu perfil?",
        rows: 4,
      },
      {
        key: "client_desires",
        label: "Quais são os desejos e ambições mais fortes do seu cliente ideal?",
        rows: 4,
      },
      {
        key: "client_needs",
        label: "Quais são as necessidades reais e imediatas do seu cliente?",
        rows: 4,
      },
      {
        key: "client_pains",
        label: "Quais são as dores, medos e frustrações do seu público?",
        rows: 4,
      },
      {
        key: "objetivo_central",
        label: "Objetivo Central do Posicionamento Digital",
        rows: 4,
      },
    ],
  },
];

export const FIELD_ALIASES: Record<string, string[]> = {
  name: ["name", "nome", "nomeCliente"],
  instagram: ["instagram", "instagramAtual", "@seuperfil"],
  whatsapp: ["whatsapp", "whatsappContato", "telefone"],
  email: ["email", "emailAcesso"],
  businessName: ["businessName", "nomeEmpresa", "empresa"],
  city: ["city", "cidade", "cidadeEstado"],
  segment: ["segment", "segmento", "nicho"],

  room_behavior: ["room_behavior", "sala_concorrentes"],
  best_skill_90: ["best_skill_90", "faz_melhor_90"],
  never_sound_like: ["never_sound_like", "nao_quer_parecer"],
  why_choose_you: ["why_choose_you", "por_que_compram", "diferencial"],
  expensive_reason: ["expensive_reason", "escolha_mais_caro"],

  mission: ["mission", "missao_marca", "missao"],
  mantra: ["mantra"],
  origin_story: ["origin_story", "historia_marca", "origemMarca"],
  controversial_opinion: ["controversial_opinion", "opiniao_polemica"],
  transformation_30d: ["transformation_30d", "pensar_30_dias"],

  instagram_purpose: ["instagram_purpose", "missao_instagram", "objetivo_central"],
  first_impression: ["first_impression", "primeira_impressao"],
  evolution_goal: ["evolution_goal", "mudanca_perfil", "meta"],
  stories_pride: ["stories_pride", "orgulho_stories"],
  save_reason: ["save_reason", "salvar_post"],

  target_audience: ["target_audience", "tipo_cliente", "clienteIdeal"],
  client_desires: ["client_desires", "desejos_publico", "desejo"],
  client_needs: ["client_needs", "necessidades_publico"],
  client_pains: ["client_pains", "dores_publico", "dorPrincipal"],
  objetivo_central: ["objetivo_central", "instagram_purpose", "missao_instagram"],
};

export function getBriefingValue(
  responses: Record<string, unknown> | undefined,
  fieldKey: string,
  clientFallback?: string | null
): string | undefined {
  if (!responses) return clientFallback || undefined;
  const aliases = FIELD_ALIASES[fieldKey] || [fieldKey];
  for (const k of aliases) {
    const val = responses[k];
    if (val !== undefined && val !== null && String(val).trim() !== "") {
      return String(val);
    }
  }
  return clientFallback || undefined;
}
